/**
 * ConvoyMissionCinematic — Mini-cinematic de fin de mission convoi
 * exceptionnel (05/02/2026, demande user).
 *
 * Suggéré comme vitrine prestige : à la complétion d'un convoi (succès ou
 * incident), un encart pleine fenêtre s'affiche pendant ~5 s avec :
 *   • Icône cargo (selon archetype.id)
 *   • Compteur de récompense qui s'incrémente (animation `ease-out`)
 *   • Bilan outcome (success / weather / breakdown / accident)
 *   • Bouton « Continuer » pour acquitter manuellement
 *
 * Auto-dismiss après 5 secondes (timeout configurable).
 *
 * Données lues : `lastConvoyCompletion` (transient state du
 * convoyExceptionnelSlice).
 * Action de fermeture : `dismissConvoyCinematic`.
 */
import React, { useEffect, useState, useRef } from 'react';
import {
  Wind,        // wind-turbine
  Rocket,      // rocket-stage
  Home,        // prefab-house
  Settings,    // industrial-press
  Anchor,      // yacht-hull
  CheckCircle2,
  CloudRain,
  Wrench,
  AlertTriangle,
  X,
} from 'lucide-react';
import useGameStore from '../../store/gameStore';

const ARCHETYPE_ICONS = {
  'Pale d\'éolienne offshore':       Wind,
  'Étage de fusée Ariane':            Rocket,
  'Maison modulaire 12×4 m':          Home,
  'Presse hydraulique 45T':           Settings,
  'Coque de yacht 22 m':              Anchor,
};

const OUTCOME_META = {
  success:    { icon: CheckCircle2,  color: 'text-emerald-300', glow: 'from-emerald-500/40 via-emerald-500/10', tone: 'Mission réussie' },
  weather:    { icon: CloudRain,     color: 'text-amber-300',   glow: 'from-amber-500/30 via-amber-500/10',     tone: 'Retard météo' },
  breakdown:  { icon: Wrench,        color: 'text-orange-300',  glow: 'from-orange-500/30 via-orange-500/10',   tone: 'Panne mécanique' },
  accident:   { icon: AlertTriangle, color: 'text-rose-300',    glow: 'from-rose-500/40 via-rose-500/10',       tone: 'Incident grave' },
};

const AUTO_DISMISS_MS = 5500;
const COUNTER_DURATION_MS = 2400;

export default function ConvoyMissionCinematic() {
  const completion = useGameStore(s => s.lastConvoyCompletion);
  const dismiss = useGameStore(s => s.dismissConvoyCinematic);
  const [displayReward, setDisplayReward] = useState(0);
  const [visible, setVisible] = useState(false);
  const rafRef = useRef(null);
  const dismissTimerRef = useRef(null);

  // Mount animation + auto-dismiss timer.
  useEffect(() => {
    if (!completion) {
      setVisible(false);
      setDisplayReward(0);
      return;
    }
    // Trigger enter animation next tick.
    const enterTimer = setTimeout(() => setVisible(true), 30);
    // Auto-dismiss.
    dismissTimerRef.current = setTimeout(() => {
      setVisible(false);
      setTimeout(() => dismiss(), 350); // wait for exit animation
    }, AUTO_DISMISS_MS);

    // Counter animation: incremental ease-out
    const target = completion.netReward || 0;
    const startTime = performance.now();
    const tick = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / COUNTER_DURATION_MS);
      // ease-out quad
      const eased = 1 - (1 - t) * (1 - t);
      setDisplayReward(Math.round(target * eased));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      clearTimeout(enterTimer);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [completion, dismiss]);

  if (!completion) return null;

  const CargoIcon = ARCHETYPE_ICONS[completion.cargo] || Settings;
  const outcomeMeta = OUTCOME_META[completion.outcome] || OUTCOME_META.success;
  const OutcomeIcon = outcomeMeta.icon;
  const sign = displayReward >= 0 ? '+' : '';

  const handleDismiss = () => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    setVisible(false);
    setTimeout(() => dismiss(), 350);
  };

  return (
    <div
      className={`fixed inset-0 z-[120] flex items-center justify-center pointer-events-none transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      data-testid="convoy-cinematic-overlay"
    >
      {/* Vignette dégradée selon outcome */}
      <div
        className={`absolute inset-0 bg-gradient-radial ${outcomeMeta.glow} to-transparent pointer-events-auto`}
        onClick={handleDismiss}
      />

      {/* Carte centrale */}
      <div
        className={`relative pointer-events-auto max-w-2xl w-[90%] bg-gradient-to-br from-ink-900/95 to-ink-800/95 backdrop-blur-md rounded-2xl border-2 ${
          completion.outcome === 'success' ? 'border-emerald-400/60' :
          completion.outcome === 'accident' ? 'border-rose-400/60' :
          completion.outcome === 'breakdown' ? 'border-orange-400/60' :
          'border-amber-400/60'
        } shadow-2xl px-10 py-8 transform transition-all duration-500 ${
          visible ? 'scale-100 translate-y-0' : 'scale-90 translate-y-4'
        }`}
        data-testid="convoy-cinematic-card"
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-slate-400 hover:text-foreground p-1.5 rounded-full hover:bg-ink-700 transition-colors"
          aria-label="Fermer"
          data-testid="convoy-cinematic-dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header — outcome */}
        <div className="flex items-center justify-center gap-2 mb-5">
          <OutcomeIcon className={`w-5 h-5 ${outcomeMeta.color}`} />
          <span className={`text-xs uppercase tracking-[0.25em] font-bold font-mono ${outcomeMeta.color}`}>
            Convoi exceptionnel — {outcomeMeta.tone}
          </span>
        </div>

        {/* Cargo icon + métadonnées */}
        <div className="flex items-center gap-6 mb-6">
          <div className={`w-24 h-24 rounded-2xl bg-ink-700/60 border border-ink-600 flex items-center justify-center flex-shrink-0 ${outcomeMeta.color}`}>
            <CargoIcon className="w-12 h-12" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-0.5">
              Mission livrée
            </p>
            <h3 className="text-xl font-bold text-foreground leading-tight mb-1">
              {completion.cargo}
            </h3>
            <p className="text-sm text-slate-400">
              Client : <strong className="text-slate-200">{completion.client}</strong>
            </p>
          </div>
        </div>

        {/* Compteur récompense */}
        <div className="text-center py-6 border-y border-ink-700/60 mb-5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-2">
            Récompense nette
          </p>
          <p
            className={`text-5xl font-mono font-black tabular-nums ${
              completion.netReward >= 0 ? 'text-money' : 'text-rose-400'
            }`}
            data-testid="convoy-cinematic-reward"
          >
            {sign}{displayReward.toLocaleString('fr-FR')} €
          </p>
          {completion.costDelta !== 0 && (
            <p className="text-[11px] text-slate-500 mt-2 font-mono">
              Forfait {completion.grossReward.toLocaleString('fr-FR')} € · Aléa {completion.costDelta.toLocaleString('fr-FR')} €
            </p>
          )}
        </div>

        {/* Outcome label détaillé + reputation */}
        <div className="flex items-center justify-between gap-4 text-xs">
          <p className="text-slate-300 flex-1 leading-relaxed italic">
            « {completion.outcomeLabel} »
          </p>
          <div className="flex-shrink-0 text-right">
            <p className="text-[9px] uppercase tracking-wider text-slate-500 font-mono">Réputation</p>
            <p className={`text-lg font-mono font-bold ${
              completion.repDelta > 0 ? 'text-emerald-300' :
              completion.repDelta < 0 ? 'text-rose-300' :
              'text-slate-300'
            }`}>
              {completion.repDelta > 0 ? '+' : ''}{completion.repDelta}
            </p>
          </div>
        </div>

        {/* Bouton continuer */}
        <button
          onClick={handleDismiss}
          className="mt-6 w-full py-2.5 rounded-lg bg-ink-700 hover:bg-ink-600 text-foreground text-sm font-bold transition-colors"
          data-testid="convoy-cinematic-continue"
        >
          Continuer
        </button>
      </div>
    </div>
  );
}
